// Fase 17.3, Plano 06 (D-19..D-21) — estado do formulário de dados técnicos da ficha: funções puras, sem React.
// O formulário guarda texto; só as chaves que mudaram vão para o PATCH (a API também aceita só as chaves enviadas).
import { interpretarNumeroPtBr, mensagemNumeroAmbiguo, type AtualizarDadosTecnicosDto, type ConsumoTintaEstimadoDto, type DadosTecnicosPecaDto, type UnidadeTinta } from '@pieltec/contracts';

export interface DadosTecnicosForm {
  readonly area: string;
  readonly corCodigo: string;
  readonly corFamilia: string;
  readonly tipoTinta: string;
  readonly pecasPorGancheira: string;
  /** 'SIM' | 'NAO' | '' (não informado). */
  readonly exigeIsolamento: string;
  readonly porte: string;
  readonly tipoBanho: string;
}

export const TEXTO_DADOS_INCOMPLETOS = 'Dados incompletos: informe área e cor para calcular o consumo de tinta.';
/** Só os dados usados pelo cálculo; tinta explícita elegível dispensa a cor. */
export function camposPendentesConsumo(dados: Pick<DadosTecnicosPecaDto, 'areaUnitariaM2' | 'corCodigo'>, usaTintaDaPeca = false): readonly { id: string; rotulo: string }[] {
  const campos = [];
  if (!dados.areaUnitariaM2?.trim()) campos.push({ id: 'dados-area', rotulo: 'Área (m²)' });
  if (!usaTintaDaPeca && !dados.corCodigo?.trim()) campos.push({ id: 'dados-cor', rotulo: 'Cor (código)' });
  return campos;
}
export const TEXTO_CONSUMO_A_DEFINIR = 'Consumo de tinta: a definir';
export const TEXTO_CONSUMO_NA_LIBERACAO = 'Consumo de tinta: calculado na liberação da OP';

export const opcoesIsolamento = [
  { value: '', label: 'Não informado' },
  { value: 'SIM', label: 'Sim' },
  { value: 'NAO', label: 'Não' },
];

/** '0.500000' (numérico do banco) -> '0,5' (como o PCP digita). */
export function areaParaTexto(area: string | null): string {
  if (area === null || area.trim() === '') return '';
  const numero = Number(area);
  if (!Number.isFinite(numero)) return area;
  return numero.toLocaleString('pt-BR', { maximumFractionDigits: 6, useGrouping: false });
}

export function formDosDados(dados: DadosTecnicosPecaDto): DadosTecnicosForm {
  return {
    area: areaParaTexto(dados.areaUnitariaM2),
    corCodigo: dados.corCodigo ?? '',
    corFamilia: dados.corFamilia ?? '',
    tipoTinta: dados.tipoTinta ?? '',
    pecasPorGancheira: dados.pecasPorGancheira === null ? '' : String(dados.pecasPorGancheira),
    exigeIsolamento: dados.exigeIsolamento === null ? '' : dados.exigeIsolamento ? 'SIM' : 'NAO',
    porte: dados.porte ?? '',
    tipoBanho: dados.tipoBanho ?? '',
  };
}

/**
 * Recarga da ficha (por exemplo depois de "Confirmar cor" ou "Ignorar"): o que o PCP digitou e ainda não salvou não se perde.
 * Cada campo que o PCP editou (difere do valor salvo anterior) mantém a edição, a menos que o valor salvo daquele campo tenha mudado
 * na recarga (o servidor vence, como a cor confirmada). Campos não editados passam a mostrar o valor salvo novo (P3-WR-06).
 */
export function mesclarFormComRecarga(atual: DadosTecnicosForm, salvoAnterior: DadosTecnicosForm, salvoNovo: DadosTecnicosForm): DadosTecnicosForm {
  const mesclado = { ...salvoNovo };
  for (const chave of Object.keys(salvoNovo) as (keyof DadosTecnicosForm)[]) {
    const editado = atual[chave] !== salvoAnterior[chave];
    const servidorMudou = salvoNovo[chave] !== salvoAnterior[chave];
    if (editado && !servidorMudou) mesclado[chave] = atual[chave];
  }
  return mesclado;
}

export type ErrosDadosTecnicos = Partial<Record<'area' | 'corCodigo' | 'pecasPorGancheira', string>>;

type LeituraArea = { readonly tipo: 'vazio' } | { readonly tipo: 'ok'; readonly texto: string } | { readonly tipo: 'invalido'; readonly mensagem: string };

const MENSAGEM_AREA = 'A área deve ser um número maior que zero, com até 6 casas decimais.';

function lerArea(texto: string): LeituraArea {
  const lido = interpretarNumeroPtBr(texto);
  if (lido.tipo === 'vazio') return { tipo: 'vazio' };
  if (lido.tipo === 'ambiguo') return { tipo: 'invalido', mensagem: mensagemNumeroAmbiguo(texto, lido) };
  if (lido.tipo !== 'ok' || !(lido.valor > 0) || lido.valor > 999999) return { tipo: 'invalido', mensagem: MENSAGEM_AREA };
  const normalizada = lido.valor.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
  return Number(normalizada) > 0 ? { tipo: 'ok', texto: normalizada } : { tipo: 'invalido', mensagem: MENSAGEM_AREA };
}

function lerGancheira(texto: string): number | null | 'invalido' {
  if (texto.trim() === '') return null;
  if (!/^\d+$/.test(texto.trim())) return 'invalido';
  const valor = Number(texto.trim());
  return valor >= 1 && valor <= 100000 ? valor : 'invalido';
}

export function validarDadosTecnicos(form: DadosTecnicosForm): ErrosDadosTecnicos {
  const erros: { area?: string; corCodigo?: string; pecasPorGancheira?: string } = {};
  const area = lerArea(form.area);
  if (area.tipo === 'invalido') erros.area = area.mensagem;
  if (form.corCodigo.trim().length > 40) erros.corCodigo = 'O código da cor aceita no máximo 40 caracteres.';
  if (lerGancheira(form.pecasPorGancheira) === 'invalido') erros.pecasPorGancheira = 'Peças por gancheira deve ser um número inteiro entre 1 e 100000.';
  return erros;
}

/** Só as chaves que mudaram em relação ao que veio da ficha; campo esvaziado vira `null` (limpa). Pressupõe `validarDadosTecnicos` sem erros. */
export function alteracoesDosDados(inicial: DadosTecnicosForm, atual: DadosTecnicosForm): AtualizarDadosTecnicosDto {
  const saida: { -readonly [K in keyof AtualizarDadosTecnicosDto]: AtualizarDadosTecnicosDto[K] } = {};
  if (atual.area.trim() !== inicial.area.trim()) {
    const area = lerArea(atual.area);
    saida.areaUnitariaM2 = area.tipo === 'ok' ? area.texto : null;
  }
  if (atual.corCodigo.trim() !== inicial.corCodigo.trim()) saida.corCodigo = atual.corCodigo.trim() === '' ? null : atual.corCodigo.trim();
  if (atual.corFamilia !== inicial.corFamilia) saida.corFamilia = atual.corFamilia === '' ? null : (atual.corFamilia as DadosTecnicosPecaDto['corFamilia']);
  if (atual.tipoTinta !== inicial.tipoTinta) saida.tipoTinta = atual.tipoTinta === '' ? null : (atual.tipoTinta as DadosTecnicosPecaDto['tipoTinta']);
  if (atual.pecasPorGancheira.trim() !== inicial.pecasPorGancheira.trim()) {
    const gancheira = lerGancheira(atual.pecasPorGancheira);
    saida.pecasPorGancheira = typeof gancheira === 'number' ? gancheira : null;
  }
  if (atual.exigeIsolamento !== inicial.exigeIsolamento) saida.exigeIsolamento = atual.exigeIsolamento === '' ? null : atual.exigeIsolamento === 'SIM';
  if (atual.porte !== inicial.porte) saida.porte = atual.porte === '' ? null : (atual.porte as DadosTecnicosPecaDto['porte']);
  if (atual.tipoBanho !== inicial.tipoBanho) saida.tipoBanho = atual.tipoBanho === '' ? null : (atual.tipoBanho as DadosTecnicosPecaDto['tipoBanho']);
  return saida;
}

/** D-21: sem área ou sem cor o consumo de tinta fica "a definir" (a peça continua com roteiro válido). */
export function dadosTecnicosCompletos(dados: Pick<DadosTecnicosPecaDto, 'areaUnitariaM2' | 'corCodigo'>): boolean {
  return dados.areaUnitariaM2 !== null && dados.corCodigo !== null;
}

export function textoConsumoDeTinta(dados: Pick<DadosTecnicosPecaDto, 'areaUnitariaM2' | 'corCodigo'>): string {
  return dadosTecnicosCompletos(dados) ? TEXTO_CONSUMO_NA_LIBERACAO : TEXTO_CONSUMO_A_DEFINIR;
}

/**
 * Fase 17.3, Plano 11 (D-21): quantidade por peça na unidade da tinta do servidor ('0.04800000', 8 casas) em pt-BR sem zeros à direita ('0,048').
 * Só texto: nenhuma conta em ponto flutuante.
 */
export function formatarKgPorPeca(kg: string): string {
  const [inteiro = '0', decimais = ''] = kg.trim().split('.');
  const aparado = decimais.replace(/0+$/, '');
  return aparado === '' ? inteiro : `${inteiro},${aparado}`;
}

export function rotuloUnidadeTinta(unidade: UnidadeTinta | null): string {
  return unidade === 'l' ? 'L' : 'kg';
}

export function textoConsumoPrevistoPorPeca(estimado: Pick<ConsumoTintaEstimadoDto, 'kgPorPeca' | 'tintaCodigo' | 'unidade' | 'rendimentoEstimado' | 'origemTinta'>): string {
  const quantidade = formatarKgPorPeca(estimado.kgPorPeca ?? '0');
  const estimativa = estimado.rendimentoEstimado ? '; rendimento estimado pelo padrão do tipo' : '';
  const origem = estimado.origemTinta === 'PECA' ? ', definida na peça' : '';
  return `Consumo de tinta previsto: ${quantidade} ${rotuloUnidadeTinta(estimado.unidade)} por peça (tinta ${estimado.tintaCodigo ?? 'não identificada'}${origem}; estimativa com o fator da célula padrão${estimativa}).`;
}

/** Quando o roteiro não tem etapa que consome a tinta da peça, o servidor devolve `A_DEFINIR` sem nenhum aviso. */
export const TEXTO_ROTEIRO_SEM_ETAPA_DE_PINTURA = 'Roteiro sem etapa de pintura.';
